<section class="company-settings">
  <ng-container *ngIf="!loading; else loadingState">
    <div *ngIf="loadError" class="state-card">
      <mat-card>
        <mat-card-header>
          <mat-card-title>Unable to load company settings</mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <p>{{ loadError }}</p>
        </mat-card-content>
        <mat-card-actions>
          <button mat-stroked-button color="primary" type="button" (click)="retry()">
            Retry
          </button>
        </mat-card-actions>
      </mat-card>
    </div>

    <form *ngIf="!loadError" [formGroup]="form" (ngSubmit)="save()" class="settings-form">
      <div class="settings-grid">
        <mat-card class="settings-card">
          <mat-card-header>
            <mat-card-title>Currency</mat-card-title>
            <mat-card-subtitle>Select the default billing currency.</mat-card-subtitle>
          </mat-card-header>
          <mat-card-content>
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Default Currency</mat-label>
              <mat-select formControlName="defaultCurrency" [compareWith]="compareCoding">
                <mat-option *ngIf="!currencyOptions.length" disabled>
                  No currency options available
                </mat-option>
                <mat-option *ngFor="let option of currencyOptions" [value]="option">
                  {{ option.display || option.code || 'Unnamed currency' }}
                  <span *ngIf="option.code" class="option-code">({{ option.code }})</span>
                </mat-option>
              </mat-select>
              <mat-error *ngIf="form.get('defaultCurrency')?.hasError('required')">
                Currency selection is required
              </mat-error>
            </mat-form-field>
            <p class="field-hint">Used for invoices, charges, and procurement calculations.</p>
          </mat-card-content>
        </mat-card>

        <mat-card class="settings-card">
          <mat-card-header>
            <mat-card-title>Observations & Reference Ranges</mat-card-title>
            <mat-card-subtitle>Configure automated behaviors for patient observations.</mat-card-subtitle>
          </mat-card-header>
          <mat-card-content>
            <div class="toggle-container">
              <mat-slide-toggle formControlName="autoCalculateInterpretation" color="primary">
                Auto-calculate Interpretation
              </mat-slide-toggle>
              <p class="field-hint">When enabled, test results will automatically show their normal ranges and be marked as normal or abnormal without needing manual approval.</p>
            </div>

            <mat-divider style="margin: 16px 0;"></mat-divider>

            <div formGroupName="manualReferenceRangePolicy">
              <div class="toggle-container">
                <mat-slide-toggle formControlName="enabled" color="primary">
                  Allow Manual Reference Ranges
                </mat-slide-toggle>
                <p class="field-hint">When enabled, staff can manually type out reference ranges for observations instead of strictly relying on auto-calculated templates.</p>
              </div>

              <div *ngIf="form.get('manualReferenceRangePolicy.enabled')?.value" style="margin-top: 16px;">
                <mat-form-field appearance="outline" class="full-width">
                  <mat-label>Allowed Roles</mat-label>
                  <mat-select formControlName="allowedRoles" multiple>
                    <mat-option *ngFor="let role of practitionerRoles" [value]="role">
                      {{ role | titlecase }}
                    </mat-option>
                  </mat-select>
                  <mat-hint>Select which staff roles are allowed to manually add or edit reference ranges.</mat-hint>
                </mat-form-field>
              </div>
            </div>
          </mat-card-content>
        </mat-card>

        <mat-card class="settings-card">
          <mat-card-header>
            <mat-card-title>Branding</mat-card-title>
            <mat-card-subtitle>Manage the organization logo used across dashboards and PDFs.</mat-card-subtitle>
          </mat-card-header>
          <mat-card-content>
            <div class="logo-preview" *ngIf="logoControl.value?.url; else noLogoState">
              <p class="field-hint">Current logo</p>
              <img [src]="logoControl.value?.url" alt="Organization logo" style="max-width: 180px; max-height: 120px; border-radius: 12px; object-fit: contain; background: var(--mat-sys-surface-variant); padding: 8px;" />
            </div>
            <ng-template #noLogoState>
              <p class="field-hint">No logo has been uploaded yet.</p>
            </ng-template>

            <app-upload-ui
              class="logo-uploader"
              [allowedFiles]="logoAllowedFilePattern"
              allowedFilesCategoryLabel="Image"
              [maxFiles]="1"
              [maxFileSize]="logoMaxFileSize"
              [existingFiles]="logoFiles"
              (filesChanged)="handleLogoFilesChanged($event)">
            </app-upload-ui>

            <p class="field-hint">Recommended: transparent PNG/JPG/SVG, 512×512px+, under 5&nbsp;MB.</p>

            <div class="g-just-flex gap-10" *ngIf="logoControl.value">
              <button mat-stroked-button type="button" (click)="clearLogo()">
                <mat-icon>delete</mat-icon>
                Remove Logo
              </button>
            </div>
          </mat-card-content>
        </mat-card>

        <!-- 
        <mat-card class="settings-card" formGroupName="lowStock">
          <mat-card-header>
            <mat-card-title>Low Stock Alerts</mat-card-title>
            <mat-card-subtitle>Configure thresholds and notification recipients.</mat-card-subtitle>
          </mat-card-header>
          <mat-card-content>
            <mat-slide-toggle formControlName="enabled">Enable stock alerts</mat-slide-toggle>
            <div class="low-stock-grid">
              <mat-form-field appearance="outline">
                <mat-label>Medication Threshold</mat-label>
                <input matInput type="number" formControlName="medicationThreshold" min="0" placeholder="e.g., 20" />
                <mat-hint>Alert when medication units fall below this value.</mat-hint>
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Supply Threshold</mat-label>
                <input matInput type="number" formControlName="supplyThreshold" min="0" placeholder="e.g., 10" />
                <mat-hint>Alert when supply packages fall below this value.</mat-hint>
              </mat-form-field>
            </div>

            <mat-divider></mat-divider>

            <div class="email-section" formArrayName="emails">
              <div class="section-header">
                <h4>Notification Emails</h4>
                <button mat-stroked-button color="primary" type="button" (click)="addNotificationEmail()">
                  <mat-icon>add</mat-icon>
                  Add Email
                </button>
              </div>
              <div
                class="email-row"
                *ngFor="let emailCtrl of lowStockEmails.controls; let i = index; trackBy: trackByIndex"
              >
                <mat-form-field appearance="outline" class="full-width">
                  <mat-label>Email Address</mat-label>
                  <input matInput type="email" [formControlName]="i" placeholder="alerts@example.com" />
                  <mat-error *ngIf="emailCtrl.invalid">
                    Provide a valid email address or leave empty
                  </mat-error>
                </mat-form-field>
                <button
                  mat-icon-button
                  type="button"
                  color="warn"
                  aria-label="Remove email"
                  (click)="removeNotificationEmail(i)"
                >
                  <mat-icon>delete</mat-icon>
                </button>
              </div>
            </div>
          </mat-card-content>
        </mat-card>
        -->

        <mat-card class="settings-card">
          <mat-card-header>
            <mat-card-title>Other Hospital Settings</mat-card-title>
            <mat-card-subtitle>Capture additional organization preferences as key/value pairs.</mat-card-subtitle>
          </mat-card-header>
          <mat-card-content formArrayName="customSettings">
            <div
              class="kv-row"
              *ngFor="let pref of customSettings.controls; let i = index; trackBy: trackByIndex"
              [formGroupName]="i"
            >
              <mat-form-field appearance="outline">
                <mat-label>Setting Name</mat-label>
                <input matInput formControlName="key" placeholder="e.g., timezone" />
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Value</mat-label>
                <input matInput formControlName="value" placeholder="e.g., Africa/Lagos" />
              </mat-form-field>
              <button
                mat-icon-button
                type="button"
                color="warn"
                aria-label="Remove setting"
                (click)="removeCustomSetting(i)"
              >
                <mat-icon>delete</mat-icon>
              </button>
            </div>
            <button mat-stroked-button color="primary" type="button" (click)="addCustomSetting()">
              <mat-icon>add</mat-icon>
              Add Setting
            </button>
          </mat-card-content>
        </mat-card>
      </div>

      <div class="actions">
        <mat-progress-bar *ngIf="saving" mode="indeterminate"></mat-progress-bar>
        <button mat-flat-button color="primary" type="submit" [disabled]="form.invalid || saving">
          <mat-icon>save</mat-icon>
          Save Settings
        </button>
      </div>
    </form>
  </ng-container>
</section>
<app-integrations-settings *ngIf="isSuperAdmin"></app-integrations-settings>
<ng-template #loadingState>
  <div class="state-card">
    <mat-card>
      <mat-card-content>
        <p>Loading company settings…</p>
        <mat-progress-bar mode="indeterminate"></mat-progress-bar>
      </mat-card-content>
    </mat-card>
  </div>
</ng-template>
